import { useMemo, useState } from 'react';
import { Input, Tag, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { GROUP_META, type PaletteEntry, type PaletteGroup } from './paletteModel';
import { resolveGlyph } from './glyphs';
import { DATA_TOUR } from '@/edu/tourModel';

const { Text } = Typography;

/** dataTransfer MIME for a palette-entry drag (read on canvas drop). */
export const PALETTE_MIME = 'application/x-ol-palette';

const GROUP_ORDER: PaletteGroup[] = ['capability', 'human', 'ai', 'control'];

export interface PaletteProps {
  entries: PaletteEntry[];
  /** The entry currently armed for click/keyboard placement (SC 2.5.7). */
  placingKey: string | null;
  onArm: (entry: PaletteEntry | null) => void;
  loading?: boolean;
}

/**
 * The node palette (spec §4.3). Drag an entry onto the canvas, OR click it to ARM placement then
 * pick a lane (the keyboard/single-pointer path — SC 2.5.7). Reserved AI primitives render but are
 * not placeable (compiler-rejected). Search filters across all groups.
 */
export function Palette({ entries, placingKey, onArm, loading }: PaletteProps) {
  const [q, setQ] = useState('');

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (e: PaletteEntry) =>
      !needle || e.label.toLowerCase().includes(needle) || e.description.toLowerCase().includes(needle);
    const out = new Map<PaletteGroup, PaletteEntry[]>();
    for (const g of GROUP_ORDER) out.set(g, []);
    for (const e of entries) {
      if (match(e)) out.get(e.group)!.push(e);
    }
    return out;
  }, [entries, q]);

  return (
    <div className="ol-palette" role="region" aria-label="Node palette" data-tour={DATA_TOUR.palette}>
      <div style={{ padding: '10px 10px 6px', borderBottom: '1px solid var(--ol-border)' }}>
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined style={{ color: 'var(--ol-text-tertiary)' }} />}
          placeholder="Search nodes…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search the node palette"
        />
        {placingKey && (
          <Text type="secondary" style={{ fontSize: 10.5, display: 'block', marginTop: 6 }}>
            Placement armed — pick a lane on the canvas, or press Esc.
          </Text>
        )}
      </div>
      <div className="ol-palette__body">
        {loading && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Loading capability families…
          </Text>
        )}
        {GROUP_ORDER.map((g) => {
          const items = grouped.get(g) ?? [];
          if (items.length === 0) return null;
          return (
            <div key={g}>
              <Tooltip title={GROUP_META[g].hint} placement="right">
                <div className="ol-palette__group">{GROUP_META[g].label}</div>
              </Tooltip>
              {items.map((e) => (
                <button
                  key={e.key}
                  type="button"
                  className="ol-palette__item"
                  aria-disabled={e.reserved || undefined}
                  data-placing={placingKey === e.key || undefined}
                  draggable={!e.reserved}
                  onDragStart={(ev) => {
                    if (e.reserved) {
                      ev.preventDefault();
                      return;
                    }
                    ev.dataTransfer.setData(PALETTE_MIME, e.key);
                    ev.dataTransfer.effectAllowed = 'copy';
                  }}
                  onClick={() => {
                    if (e.reserved) return;
                    onArm(placingKey === e.key ? null : e);
                  }}
                  style={{ ['--ol-node-accent' as string]: e.accent }}
                  title={e.reserved ? 'Reserved — the compiler rejects this kind (OL-IR-002)' : e.description}
                >
                  <span className="ol-palette__icon">{resolveGlyph(e.glyph)}</span>
                  <span style={{ minWidth: 0 }}>
                    <span className="ol-palette__label">
                      {e.label}
                      {e.reserved && (
                        <Tag bordered={false} style={{ fontSize: 8.5, marginInlineStart: 4, lineHeight: '14px' }}>
                          reserved
                        </Tag>
                      )}
                    </span>
                    <span className="ol-palette__desc">{e.description}</span>
                  </span>
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
